import { Component, HostBinding, HostListener } from '@angular/core';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BrickDefinition } from 'src/app/samwebDescription/samweb/brickDefinition';
import { BrickGainedFocus, BrickLostFocus } from 'src/app/store/dashboard/dashboard.actions';
import { HOTKEYS_DISABLED } from 'src/app/store/samwin/shortcut-behaviour/hotkeys-disabled';
import { shortcutStringBuilderFromKeyboardEvent } from '../../helper/shortcutStringBuilder';
import { ShortcutDescription } from '../../samwebDescription/samweb/shortcuts/shortcutDescription';
import { animate, style, transition, trigger } from '@angular/animations';

const HOTKEY_TIME_OUT = 10;

@Component({
   template: '',
   animations: [trigger('fadeInOut', [transition(':leave', [animate(250, style({ opacity: 0 }))])])],
})
export abstract class BasicBrickComponent {
   public brickInstanceId: Readonly<string>;
   public brickDefinition: Readonly<BrickDefinition>;
   public baseStore: Store;
   public keyActions: ShortcutDescription[] = [];
   private isTimedOut = false;

   @HostBinding('@fadeInOut') in = true;

   public getShortcutDescription$(hotKey: Observable<string>, actionToExecute: () => boolean): Observable<ShortcutDescription> {
      return hotKey.pipe(
         map((hotkey) => {
            return {
               shortcutKey: hotkey,
               action: actionToExecute,
            };
         })
      );
   }

   private handleKeyPressed(key: string): boolean {
      const keyAction = _(this.keyActions)
         .filter((x) => x.shortcutKey === key)
         .head();

      if (_.isFunction(keyAction?.action)) {
         return keyAction.action();
      }
      return false;
   }

   // keypress can only be prevented on keydown
   @HostListener('keydown', ['$event'])
   private onKeyDown(eventData: KeyboardEvent) {
      if (eventData.repeat) {
         // Set time out for hotkey, to prevent memory overflow on holding the key.
         if (this.isTimedOut) {
            eventData.preventDefault();
            eventData.stopPropagation();
            return;
         }
         this.isTimedOut = true;
         this.handleKeyEvent(eventData);
         setTimeout(() => {
            this.isTimedOut = false;
         }, HOTKEY_TIME_OUT);
      } else {
         this.handleKeyEvent(eventData);
      }
   }

   @HostListener('keyup', ['$event'])
   private onKeyUp(eventData: KeyboardEvent) {
      // PrintScreen and Meta key are only triggered on keyup
      if (eventData.code !== 'PrintScreen' || !eventData.metaKey) {
         return;
      }
      this.handleKeyEvent(eventData);
   }

   private handleKeyEvent(eventData: KeyboardEvent) {
      if (_.some(this.keyActions, (x) => x.shortcutKey === HOTKEYS_DISABLED)) {
         eventData.stopPropagation();
         return;
      }
      const shortcutString = shortcutStringBuilderFromKeyboardEvent(eventData);
      const shortcutHandled = this.handleKeyPressed(shortcutString);
      if (shortcutHandled) {
         eventData.preventDefault();
         eventData.stopPropagation();
      }
   }

   private canGainFocus(): boolean {
      if (_.isEmpty(this.brickInstanceId)) {
         return false;
      }

      // if not explicitly defined, all non-layout bricks can gain focus
      if (!_.isNil(this.brickDefinition?.canGainFocus)) {
         return this.brickDefinition.canGainFocus;
      }
      if (!_.isNil(this.brickDefinition?.isLayoutBrick)) {
         return !this.brickDefinition.isLayoutBrick;
      }
      return true;
   }

   @HostListener('focusin', ['$event'])
   private onFocusIn(eventData: FocusEvent) {
      if (!this.canGainFocus()) {
         return;
      }

      this.baseStore.dispatch(new BrickGainedFocus(this.brickInstanceId));
   }

   @HostListener('focusout', ['$event'])
   private onFocusOut(eventData: FocusEvent) {
      if (!this.canGainFocus()) {
         return;
      }

      this.baseStore.dispatch(new BrickLostFocus(this.brickInstanceId));
   }
}
